import { apiClient } from '@/lib/api-client';
import type { AuthResponse, LoginMethodResponse, TotpSetupResponse, UserProfile } from '@/types/auth';

export const authService = {
  login(email: string, password: string) {
    return apiClient<AuthResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuth: true,
    });
  },

  requestOtp(email: string) {
    return apiClient<{ message: string }>('/auth/otp', {
      method: 'POST',
      body: { email },
      skipAuth: true,
    });
  },

  verifyOtp(email: string, otp: string) {
    return apiClient<AuthResponse>('/auth/verify', {
      method: 'POST',
      body: { email, otp },
      skipAuth: true,
    });
  },

  refreshSession() {
    return apiClient<AuthResponse>('/auth/refresh', {
      method: 'POST',
      skipAuth: true,
    });
  },

  getMe() {
    return apiClient<UserProfile>('/auth/me');
  },

  logout() {
    return apiClient<{ message: string }>('/auth/logout', {
      method: 'POST',
    });
  },

  /** Ends every session this account holds, this browser included. */
  logoutAll() {
    return apiClient<{ message: string; sessions_ended: number }>('/auth/logout-all', {
      method: 'POST',
    });
  },

  // ─── TOTP / MFA ─────────────────────────────────────────────────────────

  getLoginMethod(email: string) {
    return apiClient<LoginMethodResponse>('/auth/login-method', {
      method: 'POST',
      body: { email },
      skipAuth: true,
    });
  },

  verifyTotp(email: string, code: string) {
    return apiClient<AuthResponse>('/auth/totp/verify', {
      method: 'POST',
      body: { email, code },
      skipAuth: true,
    });
  },

  verifyTotpRecovery(email: string, recovery_code: string) {
    return apiClient<AuthResponse>('/auth/totp/recovery', {
      method: 'POST',
      body: { email, recovery_code },
      skipAuth: true,
    });
  },

  initTotpSetup(email: string, otp: string) {
    return apiClient<TotpSetupResponse>('/auth/totp/setup/init', {
      method: 'POST',
      body: { email, otp },
      skipAuth: true,
    });
  },

  confirmTotpSetupPublic(setup_session: string, code: string) {
    return apiClient<AuthResponse>('/auth/totp/setup/confirm', {
      method: 'POST',
      body: { setup_session, code },
      skipAuth: true,
    });
  },

  setupTotp() {
    return apiClient<TotpSetupResponse>('/auth/totp/setup', {
      method: 'POST',
    });
  },

  confirmTotpSetup(code: string) {
    return apiClient<{ message: string }>('/auth/totp/setup/self-confirm', {
      method: 'POST',
      body: { code },
    });
  },

  disableTotp(code: string) {
    return apiClient<{ message: string }>('/auth/totp', {
      method: 'DELETE',
      body: { code },
    });
  },

  enableMfaForUser(user_id: string) {
    return apiClient<{ message: string }>('/auth/totp/enable-user', {
      method: 'POST',
      body: { user_id },
    });
  },

  disableMfaForUser(user_id: string) {
    return apiClient<{ message: string }>('/auth/totp/disable-user', {
      method: 'DELETE',
      body: { user_id },
    });
  },
};
